import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CoachingCaseFormat } from '@euler/model/coaching';
import {
  CoachingPracticeReportView,
  CoachingPracticeSpeaker,
} from '@euler/model/coaching-practice';
import { memo, useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { aiColors } from '../../../ai-suite/theme';
import { Chip } from '../../components/Chip';
import { compressLevels } from '../functions/traineeVoice';
import { LinePosition, LineVoice } from '../functions/useScenePlayback';
import {
  kAnchorColors,
  kAnchorLabels,
  kDrillResultLabels,
  kEndingLabels,
  scenePillText,
} from '../practiceCopy';
import { CustomerBubble } from '../scene/components/dialogue/CustomerBubble';
import { ScenePill } from '../scene/components/dialogue/ScenePill';
import { TraineeBubble } from '../scene/components/dialogue/TraineeBubble';
import { RetryTag } from './components/RetryTag';

/**
 * The whole conversation as the scene's thread: customer lines, the same
 * bubbles as live, can be played again (their waveform and length show
 * once voiced);
 * under the trainee's, how each criterion went on them (带提示 after a
 * hint), a critical error where it was said, and 值得重练 on a moment.
 */
export const TranscriptView = memo(
  ({
    report,
    playing,
    voices,
    position$,
    onPlay,
    traineePlaying,
    onPlayTrainee,
    onRetryMoment,
  }: {
    report: CoachingPracticeReportView;
    /** The customer turn being voiced. */
    playing: number | null;
    /** The customer lines voiced so far, by turn. */
    voices: ReadonlyMap<number, LineVoice>;
    position$: BehaviorSubject<LinePosition | null>;
    onPlay: (turn: number) => void;
    /** The trainee line playing, by its stored voice's id. */
    traineePlaying: string | null;
    onPlayTrainee: (voiceId: string) => void;
    onRetryMoment: (turn: number) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    // Each line's stored voice, by speaker and turn.
    const stored = useMemo(
      () =>
        // A backend that predates stored voices lists none.
        new Map(
          (report.voices ?? []).map(
            x => [`${x.speaker}:${x.turn}`, x] as const,
          ),
        ),
      [report.voices],
    );
    const storedOf = (speaker: CoachingPracticeSpeaker, turn: number) =>
      stored.get(`${speaker}:${turn}`) ?? null;
    const critical = new Set(
      report.criticalErrors.flatMap(x => (x.turn ? [x.turn] : [])),
    );
    const moments = new Set(report.exam ? [] : report.moments.map(x => x.turn));
    const traineeOpened =
      report.transcript[0]?.speaker === CoachingPracticeSpeaker.Trainee;
    const drill = report.format === CoachingCaseFormat.Drill;
    const result = drill
      ? report.end.drillResult && kDrillResultLabels[report.end.drillResult]
      : report.end.ending && kEndingLabels[report.end.ending];
    return (
      <ScrollView
        contentContainerStyle={{
          width: '100%',
          maxWidth: rpx(720),
          alignSelf: 'center',
          paddingHorizontal: rpx(16),
          paddingTop: rpx(16),
          paddingBottom: rpx(32),
          gap: rpx(16),
        }}
      >
        <ScenePill
          text={scenePillText(
            report.channel,
            traineeOpened ? '你' : report.customer.name,
          )}
        />
        {report.transcript.map((line, i) => {
          if (line.speaker === CoachingPracticeSpeaker.Customer)
            return (
              <CustomerBubble
                key={`${line.turn}-c-${i}`}
                name={report.customer.name}
                text={line.text}
                // As live: WeChat lines are read, not heard.
                turn={line.turn}
                playing={playing === line.turn}
                voicing={false}
                // Measured as played, else as stored: shown before playing.
                voice={
                  voices.get(line.turn) ??
                  storedOf(CoachingPracticeSpeaker.Customer, line.turn)
                }
                position$={position$}
                onPlay={onPlay}
              />
            );
          const spoken = storedOf(CoachingPracticeSpeaker.Trainee, line.turn);
          const tags = report.criteria.filter(x => x.turn === line.turn);
          const marked =
            critical.has(line.turn) || tags.length || moments.has(line.turn);
          return (
            <View key={`${line.turn}-t-${i}`} style={{ gap: rpx(4) }}>
              <TraineeBubble
                text={line.text}
                voice={
                  spoken
                    ? {
                        levels: compressLevels(
                          spoken.levels,
                          spoken.durationMs,
                        ),
                        durationMs: spoken.durationMs,
                      }
                    : null
                }
                playing={spoken != null && traineePlaying === spoken.id}
                onPlay={spoken ? () => onPlayTrainee(spoken.id) : null}
              />
              {marked ? (
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    gap: rpx(4),
                    paddingRight: rpx(4),
                  }}
                >
                  {critical.has(line.turn) ? (
                    <Chip text="关键失误" color={aiColors.destructive} />
                  ) : null}
                  {tags.map(x => (
                    <Chip
                      key={x.criterionRef}
                      text={`${x.name} · ${kAnchorLabels[x.anchor]}${
                        x.hinted ? ' · 带提示' : ''
                      }`}
                      color={kAnchorColors[x.anchor]}
                    />
                  ))}
                  {moments.has(line.turn) ? (
                    <RetryTag onPress={() => onRetryMoment(line.turn)} />
                  ) : null}
                </View>
              ) : null}
            </View>
          );
        })}
        {result ? (
          <View style={{ paddingTop: rpx(8) }}>
            <ScenePill text={`结果：${result}`} outlined />
          </View>
        ) : null}
      </ScrollView>
    );
  },
);
